import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import getApiUrl from '../../config/api';
import useActiveFirm from '../../hooks/useActiveFirm';
import 'react-datepicker/dist/react-datepicker.css';
import './SalarySystem.css';
import './NewSalaryStatementsPage.css';

// Components
import SalaryStatementsHeader from './components/SalaryStatementsHeader';
import SalaryStatementsTable from './components/SalaryStatementsTable';
import DailyDistributionModal from './components/DailyDistributionModal';
import CustomPaginationWithManyPages from '../layout/CustomPaginationWithManyPages';

// Actions
import { getSalaryStatementForDateRange } from '../../actions/salarySystem';

// Utils
import { getDateColumns, createDateFilter } from './utils/dateUtils';
import { getDailyRate, getDailyAmount, getExpectedDailyAmount, getProductionDailyAmount } from './utils/calculationUtils';
import { formatCurrency } from './utils/formatUtils';

const NewSalaryStatementsPage = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { firmId, firmName, activeFirm } = useActiveFirm();
    const [attendanceData, setAttendanceData] = useState({});
    
    // Daily distribution modal state
    const [showDistributionModal, setShowDistributionModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    
    // Local state for date range selector
    const [localDateRange, setLocalDateRange] = useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const fromDateParam = urlParams.get('from_date');
        const toDateParam = urlParams.get('to_date');
        
        if (fromDateParam && toDateParam) {
            return [moment(fromDateParam), moment(toDateParam)];
        }
        
        const today = moment();
        const startOfMonth = today.clone().startOf('month');
        return [startOfMonth, today.clone()];
    });

    const [startDateMoment, endDateMoment] = localDateRange;
    const startDate = startDateMoment ? startDateMoment.toDate() : null;
    const endDate = endDateMoment ? endDateMoment.toDate() : null;

    // Get salary statement data from Redux
    const {
        salaryStatement: salaryStatementData,
        salaryStatementLoading,
        salaryStatementCurrentPage,
        salaryStatementNumberOfPages,
        salaryStatementTotal,
        salaryStatementPerPage
    } = useSelector(state => state.salarySystem);
    
    // Transform Redux data to component format
    // Handle both array and object with data property
    const dataArray = Array.isArray(salaryStatementData) 
        ? salaryStatementData 
        : (salaryStatementData?.data && Array.isArray(salaryStatementData.data) 
            ? salaryStatementData.data 
            : []);
    
    // Helper function to sanitize numeric values (convert "не число" and invalid values to 0)
    const sanitizeValue = (value) => {
        if (typeof value === 'string' && (value.trim() === 'не число' || value.trim() === '')) {
            return 0;
        }
        const num = parseFloat(value);
        return isNaN(num) ? 0 : num;
    };
    
    const salaryData = dataArray.map((item, index) => {
        // Sanitize daily_earnings to convert "не число" strings to 0
        const sanitizedDailyEarnings = (item.daily_earnings || []).map(day => ({
            ...day,
            attendance_amount: sanitizeValue(day.attendance_amount),
            production_amount: sanitizeValue(day.production_amount)
        }));
        
        return {
            id: item.employee_id || index + 1,
            employee: item.full_name || item.employee_name || 'Unknown Employee',
            salary: sanitizeValue(item.monthly_base_rate || item.salary_amount),
            previousRemainder: sanitizeValue(item.previous_balance),
            processed: sanitizeValue(item.processed_days),
            earned: sanitizeValue(item.total_earned),
            paid: sanitizeValue(item.total_paid),
            currentRemainder: sanitizeValue(item.current_balance),
            daily_earnings: sanitizedDailyEarnings,
            employee_type: item.employee_type || 'volume',
            icon: item.employee_type === 'volume' ? '⭐' : (item.employee_type === 'fixed' ? '👤' : '')
        };
    });
    
    // Debug: Check if data exists
    if (salaryStatementData && !Array.isArray(salaryStatementData) && !salaryStatementData?.data) {
        console.warn('salaryStatementData is not an array:', salaryStatementData);
    }
    
    const loading = salaryStatementLoading;
    
    // Get firm data
    const [currentFirmData, setCurrentFirmData] = useState(null);
    
    useEffect(() => {
        const fetchFirmData = async () => {
            if (!firmId) return;
            try {
                const firmResponse = await axios.get(`${getApiUrl()}/firms/manager-options`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (firmResponse.data) {
                    const selectedFirmData = firmResponse.data.find(f => f.id.toString() === firmId.toString());
                    setCurrentFirmData(selectedFirmData);
                }
            } catch (error) {
                console.error('Failed to fetch firm data:', error);
            }
        };
        fetchFirmData();
    }, [firmId]);
    
    // Memoize date strings to prevent infinite loops
    const fromDateString = useMemo(() => startDateMoment?.format('YYYY-MM-DD'), [startDateMoment]);
    const toDateString = useMemo(() => endDateMoment?.format('YYYY-MM-DD'), [endDateMoment]);
    
    // Use ref to track if we've already fetched to prevent duplicate fetches
    const hasFetchedRef = useRef(false);
    const lastFetchParamsRef = useRef({ firm_id: null, from_date: null, to_date: null, page: null });
    const isInitialMountRef = useRef(true);
    
    // Fetch data when firm or date range changes, and update URL
    // CustomPaginationWithManyPages will handle page changes only
    useEffect(() => {
        if (firmId && startDateMoment && endDateMoment) {
            const urlParams = new URLSearchParams(location.search);
            const fromDate = fromDateString;
            const toDate = toDateString;
            const page = parseInt(urlParams.get('page')) || 1;
            const perPage = parseInt(urlParams.get('per_page')) || 10;
            
            // Check if we've already fetched with these exact parameters
            const currentParams = { firm_id: firmId, from_date: fromDate, to_date: toDate, page };
            const lastParams = lastFetchParamsRef.current;
            const paramsChanged = 
                lastParams.firm_id !== currentParams.firm_id ||
                lastParams.from_date !== currentParams.from_date ||
                lastParams.to_date !== currentParams.to_date ||
                lastParams.page !== currentParams.page;
            
            // Update URL with date params so they're available for pagination (but not firm_id as it's global)
            urlParams.set('from_date', fromDate);
            urlParams.set('to_date', toDate);
            urlParams.set('page', page.toString());
            urlParams.set('per_page', perPage.toString());
            
            const newUrl = `${location.pathname}?${urlParams.toString()}`;
            const urlChanged = location.search !== `?${urlParams.toString()}`;
            
            if (urlChanged) {
                history.replace(newUrl);
            }
            
            // Fetch on initial mount, then only when parameters change
            if (isInitialMountRef.current) {
                isInitialMountRef.current = false;
                hasFetchedRef.current = true;
                lastFetchParamsRef.current = currentParams;
                
                // Do initial fetch
                dispatch(getSalaryStatementForDateRange(page, {
                    firm_id: firmId,
                    from_date: fromDate,
                    to_date: toDate,
                    per_page: perPage
                }));
            } else if (paramsChanged) {
                hasFetchedRef.current = true;
                lastFetchParamsRef.current = currentParams;
                
                // Fetch data when parameters change
                dispatch(getSalaryStatementForDateRange(page, {
                    firm_id: firmId,
                    from_date: fromDate,
                    to_date: toDate,
                    per_page: perPage
                }));
            }
        }
    }, [firmId, fromDateString, toDateString, dispatch, history, location]);
    
    // Action creator for CustomPaginationWithManyPages
    // Must match signature: (page, params) => async (dispatch) => {...}
    // Same as getProducts: (page, filters) => async (dispatch) => {...}
    // Use memoized date strings to prevent callback from changing
    const handlePaginationAction = useCallback((page, params) => {
        return async (dispatch) => {
            // Ensure params have all required fields - if missing, get from URL or current state
            const urlParams = new URLSearchParams(window.location.search);
            const finalParams = {
                firm_id: firmId, // Always use global active firm
                from_date: params?.from_date || urlParams.get('from_date') || fromDateString,
                to_date: params?.to_date || urlParams.get('to_date') || toDateString,
                per_page: params?.per_page || urlParams.get('per_page') || salaryStatementPerPage || 10
            };
            
            return dispatch(getSalaryStatementForDateRange(page, finalParams));
        };
    }, [firmId, fromDateString, toDateString, salaryStatementPerPage]);
    


    const handleDateRangeChange = (dates) => {
        const [start, end] = dates;
        
        if (start) {
            const startMoment = moment(start);
            const endMoment = end ? moment(end) : null;
            
            // Update URL with date params immediately
            const currentPath = location.pathname;
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('from_date', startMoment.format('YYYY-MM-DD'));
            if (endMoment) {
                searchParams.set('to_date', endMoment.format('YYYY-MM-DD'));
            }
            // Reset pagination when date changes
            searchParams.set('page', '1');
            const newUrl = `${currentPath}?${searchParams.toString()}`;
            history.push(newUrl);
            
            // Validate that both dates are in the same month
            if (startMoment && endMoment) {
                const startMonth = startMoment.month();
                const startYear = startMoment.year();
                const endMonth = endMoment.month();
                const endYear = endMoment.year();
                
                if (startMonth !== endMonth || startYear !== endYear) {
                    console.warn('Date range must be within the same month. Resetting end date.');
                    setLocalDateRange([startMoment, null]);
                    return;
                }
            }
            
            setLocalDateRange([startMoment, endMoment]);
            
            if (start && end) {
                const searchParams = new URLSearchParams(location.search);
                searchParams.set('from_date', startMoment.format('YYYY-MM-DD'));
                searchParams.set('to_date', endMoment.format('YYYY-MM-DD'));
                const newUrl = `${location.pathname}?${searchParams.toString()}`;
                history.push(newUrl);
            }
        }
    };

    const handleAttendanceChange = (employeeId, dateKey, shiftMultiplier) => {
        const attendanceKey = `${employeeId}-${dateKey}`;
        setAttendanceData(prev => ({
            ...prev,
            [attendanceKey]: shiftMultiplier
        }));
    };

    const getAttendancePercentage = (employeeId, dateKey) => {
        const attendanceKey = `${employeeId}-${dateKey}`;
        return attendanceData[attendanceKey] || '1.0';
    };

    const handleDateColumnClick = (dateKey) => {
        if (!firmId) {
            console.log('No firm selected, cannot open modal');
            return;
        }
        
        setSelectedDate(dateKey);
        setShowDistributionModal(true);
    };


    // Derived values
    const dateColumns = getDateColumns(startDateMoment, endDateMoment);
    const filterDate = createDateFilter(startDateMoment, endDateMoment);
    // No need to slice - data is already paginated from backend
    const paginatedData = salaryData;
    const workingDaysPerMonth = currentFirmData?.working_days_per_month || 26;

    // Helper functions
    const getDailyRateForItem = (item) => getDailyRate(item, workingDaysPerMonth);

    // Show loading state while waiting for firm to be auto-selected
    if (!firmId) {
        return (
            <div className="new-salary-statements">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="new-salary-statements">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading salary statements...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="new-salary-statements">
            <SalaryStatementsHeader
                startDate={startDate}
                endDate={endDate}
                onDateRangeChange={handleDateRangeChange}
                filterDate={filterDate}
            />

            <SalaryStatementsTable
                salaryData={paginatedData}
                dateColumns={dateColumns}
                onDateColumnClick={handleDateColumnClick}
                getDailyAmount={getDailyAmount}
                getExpectedDailyAmount={getExpectedDailyAmount}
                getProductionDailyAmount={getProductionDailyAmount}
                getDailyRate={getDailyRateForItem}
                getAttendancePercentage={getAttendancePercentage}
                formatCurrency={formatCurrency}
                attendanceData={attendanceData}
                currentPage={salaryStatementCurrentPage}
                itemsPerPage={salaryStatementPerPage}
            />
            
            {salaryStatementTotal > 0 && (
                <div className="row mt-3">
                    <div className="col-xl-12 col-xxl-12 d-flex">
                        <CustomPaginationWithManyPages
                            numberOfPages={salaryStatementNumberOfPages}
                            getPageItems={handlePaginationAction}
                            current_page={salaryStatementCurrentPage}
                            custom_params={{ 
                                firm_id: firmId,
                                from_date: fromDateString,
                                to_date: toDateString,
                                per_page: salaryStatementPerPage
                            }}
                        />
                    </div>
                </div>
            )}
            
            {/* Display pagination info */}
            {salaryStatementTotal > 0 && (
                <div className="pagination-info mt-2 mb-2 text-center">
                    <span>
                        Показано {((salaryStatementCurrentPage - 1) * salaryStatementPerPage) + 1}-{Math.min(salaryStatementCurrentPage * salaryStatementPerPage, salaryStatementTotal)} из {salaryStatementTotal} сотрудников
                    </span>
                </div>
            )}

            <DailyDistributionModal
                isOpen={showDistributionModal}
                onClose={() => setShowDistributionModal(false)}
                selectedDate={selectedDate}
                selectedFirm={firmId}
                salaryData={salaryData}
                getDailyRate={getDailyRateForItem}
                formatCurrency={formatCurrency}
                onDataSaved={() => {
                    // Refetch data after saving
                    const urlParams = new URLSearchParams(location.search);
                    const page = parseInt(urlParams.get('page')) || 1;
                    dispatch(getSalaryStatementForDateRange(page, {
                        firm_id: firmId,
                        from_date: fromDateString,
                        to_date: toDateString,
                        per_page: salaryStatementPerPage
                    }));
                }}
                currentFirmData={currentFirmData}
            />
        </div>
    );
};

export default NewSalaryStatementsPage;
